import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import {
  LayoutDashboard,
  FileText,
  Users,
  Mail,
  LogOut,
  Plus,
  Edit,
  Trash2,
  Eye,
  X,
  Save,
  ChevronDown,
  Search,
  Clock,
  CheckCircle,
  XCircle,
  MessageSquare,
  User
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` }
});

const STATUS_COLORS = {
  pending: "bg-amber-100 text-amber-800",
  reviewed: "bg-blue-100 text-blue-800",
  contacted: "bg-purple-100 text-purple-800",
  accepted: "bg-green-100 text-green-800",
  declined: "bg-red-100 text-red-800"
};

const NEWSLETTER_STATUS_COLORS = {
  draft: "bg-gray-100 text-gray-800",
  published: "bg-green-100 text-green-800"
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");
  const [admin, setAdmin] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  // Newsletter state
  const [newsletters, setNewsletters] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [showNewsletterEditor, setShowNewsletterEditor] = useState(false);
  const [editingNewsletter, setEditingNewsletter] = useState(null);
  const [newsletterForm, setNewsletterForm] = useState({ title: "", subtitle: "", content: "", status: "draft" });

  // Applications state
  const [applications, setApplications] = useState([]);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [applicationFilter, setApplicationFilter] = useState("all");
  const [applicationSearch, setApplicationSearch] = useState("");
  const [applicationNotes, setApplicationNotes] = useState("");

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, newslettersRes, subscribersRes, applicationsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, getAuthHeaders()),
        axios.get(`${API}/admin/newsletters`, getAuthHeaders()),
        axios.get(`${API}/admin/subscribers`, getAuthHeaders()),
        axios.get(`${API}/admin/applications`, getAuthHeaders())
      ]);
      
      setStats(statsRes.data);
      setNewsletters(newslettersRes.data);
      setSubscribers(subscribersRes.data);
      setApplications(applicationsRes.data);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem("admin_token");
        localStorage.removeItem("admin_info");
        navigate("/admin");
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    const adminInfo = localStorage.getItem("admin_info");
    
    if (!token) {
      navigate("/admin");
      return;
    }
    
    if (adminInfo) {
      setAdmin(JSON.parse(adminInfo));
    }
    
    fetchData();
  }, [navigate, fetchData]);

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_info");
    navigate("/admin");
  };

  // Newsletter handlers
  const handleSaveNewsletter = async () => {
    try {
      if (editingNewsletter) {
        await axios.put(`${API}/admin/newsletters/${editingNewsletter.id}`, newsletterForm, getAuthHeaders());
        toast.success("Newsletter updated");
      } else {
        await axios.post(`${API}/admin/newsletters`, newsletterForm, getAuthHeaders());
        toast.success("Newsletter created");
      }
      setShowNewsletterEditor(false);
      setEditingNewsletter(null);
      setNewsletterForm({ title: "", subtitle: "", content: "", status: "draft" });
      fetchData();
    } catch (error) {
      toast.error("Failed to save newsletter");
    }
  };

  const handleDeleteNewsletter = async (id) => {
    if (!window.confirm("Are you sure you want to delete this newsletter?")) return;
    try {
      await axios.delete(`${API}/admin/newsletters/${id}`, getAuthHeaders());
      toast.success("Newsletter deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete newsletter");
    }
  };

  const openNewsletterEditor = (newsletter = null) => {
    if (newsletter) {
      setEditingNewsletter(newsletter);
      setNewsletterForm({ 
        title: newsletter.title || newsletter.subject || "", 
        subtitle: newsletter.subtitle || "",
        content: newsletter.content, 
        status: newsletter.status 
      });
    } else {
      setEditingNewsletter(null);
      setNewsletterForm({ title: "", subtitle: "", content: "", status: "draft" });
    }
    setShowNewsletterEditor(true);
  };

  // Application handlers
  const handleUpdateApplication = async (id, updates) => {
    try {
      console.log("Updating application:", id, updates);
      const response = await axios.put(`${API}/admin/applications/${id}`, updates, getAuthHeaders());
      console.log("Update response:", response.data);
      toast.success("Application updated");
      fetchData();
      if (selectedApplication?.id === id) {
        setSelectedApplication({ ...selectedApplication, ...updates });
      }
    } catch (error) {
      console.error("Update error:", error.response?.data || error.message);
      toast.error(error.response?.data?.detail || "Failed to update application");
    }
  };

  const handleDeleteApplication = async (id) => {
    if (!window.confirm("Are you sure you want to delete this application?")) return;
    try {
      await axios.delete(`${API}/admin/applications/${id}`, getAuthHeaders());
      toast.success("Application deleted");
      setSelectedApplication(null);
      fetchData();
    } catch (error) {
      toast.error("Failed to delete application");
    }
  };

  const filteredApplications = applications.filter(app => {
    const matchesFilter = applicationFilter === "all" || app.status === applicationFilter;
    const matchesSearch = !applicationSearch || 
      app.first_name?.toLowerCase().includes(applicationSearch.toLowerCase()) ||
      app.last_name?.toLowerCase().includes(applicationSearch.toLowerCase()) ||
      app.email?.toLowerCase().includes(applicationSearch.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F0EA] flex items-center justify-center">
        <div className="animate-pulse text-graphite/50">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F0EA]" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-full w-64 bg-graphite-dark text-bone p-6 flex flex-col">
        <div className="mb-12">
          <h1 className="font-heading text-lg tracking-[0.15em] uppercase">Elise Redfield</h1>
          <p className="font-body text-xs text-bone/50 mt-1">Admin Dashboard</p>
        </div>

        <nav className="flex-1 space-y-2">
          {[
            { id: "overview", label: "Overview", icon: LayoutDashboard },
            { id: "newsletters", label: "Newsletters", icon: Mail },
            { id: "applications", label: "Applications", icon: FileText },
            { id: "subscribers", label: "Subscribers", icon: Users }
          ].map(item => (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded transition-all ${
                activeTab === item.id 
                  ? "bg-bone/10 text-bone" 
                  : "text-bone/60 hover:text-bone hover:bg-bone/5"
              }`}
              data-testid={`nav-${item.id}`}
            >
              <item.icon size={18} />
              <span className="font-body text-sm">{item.label}</span>
            </button>
          ))}
        </nav>

        <div className="border-t border-bone/10 pt-6 mt-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-bone/10 flex items-center justify-center">
              <User size={18} className="text-bone/60" />
            </div>
            <div>
              <p className="font-body text-sm text-bone">{admin?.name}</p>
              <p className="font-body text-xs text-bone/50">{admin?.email}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 text-bone/60 hover:text-bone hover:bg-bone/5 rounded transition-all"
            data-testid="logout-btn"
          >
            <LogOut size={18} />
            <span className="font-body text-sm">Sign Out</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="ml-64 p-8">
        {/* Overview Tab */}
        {activeTab === "overview" && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            <h2 className="font-heading text-3xl tracking-wide uppercase text-graphite-dark mb-8">
              Dashboard Overview
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
              {[
                { label: "Total Applications", value: stats?.total_applications || 0, icon: FileText, color: "bg-blue-50" },
                { label: "Pending Review", value: stats?.pending_applications || 0, icon: Clock, color: "bg-amber-50" },
                { label: "Newsletter Subscribers", value: stats?.total_subscribers || 0, icon: Users, color: "bg-green-50" },
                { label: "Newsletters Created", value: stats?.total_newsletters || 0, icon: Mail, color: "bg-purple-50" }
              ].map((stat, i) => (
                <div key={i} className={`${stat.color} p-6 rounded-lg border border-graphite/5`}>
                  <stat.icon size={24} className="text-graphite/40 mb-4" />
                  <p className="font-heading text-3xl text-graphite-dark mb-1">{stat.value}</p>
                  <p className="font-body text-sm text-graphite/60">{stat.label}</p>
                </div>
              ))}
            </div>

            {/* Quick Actions */}
            <div className="bg-white p-6 rounded-lg border border-graphite/5">
              <h3 className="font-heading text-lg tracking-wide uppercase text-graphite-dark mb-4">
                Quick Actions
              </h3>
              <div className="flex gap-4">
                <button
                  onClick={() => { setActiveTab("newsletters"); openNewsletterEditor(); }}
                  className="flex items-center gap-2 px-4 py-2 bg-graphite-dark text-bone rounded hover:bg-black transition-colors"
                >
                  <Plus size={18} />
                  <span className="font-body text-sm">New Newsletter</span>
                </button>
                <button
                  onClick={() => setActiveTab("applications")}
                  className="flex items-center gap-2 px-4 py-2 border border-graphite/20 text-graphite-dark rounded hover:bg-graphite/5 transition-colors"
                >
                  <Eye size={18} />
                  <span className="font-body text-sm">Review Applications</span>
                </button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Newsletters Tab */}
        {activeTab === "newsletters" && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            <div className="flex items-center justify-between mb-8">
              <h2 className="font-heading text-3xl tracking-wide uppercase text-graphite-dark">
                Newsletters
              </h2>
              <button
                onClick={() => openNewsletterEditor()}
                className="flex items-center gap-2 px-4 py-2 bg-graphite-dark text-bone rounded hover:bg-black transition-colors"
                data-testid="new-newsletter-btn"
              >
                <Plus size={18} />
                <span className="font-body text-sm">New Newsletter</span>
              </button>
            </div>

            {/* Newsletter List */}
            <div className="bg-white rounded-lg border border-graphite/5 overflow-hidden">
              {newsletters.length === 0 ? (
                <div className="p-12 text-center">
                  <Mail size={48} className="text-graphite/20 mx-auto mb-4" />
                  <p className="font-body text-graphite/50">No newsletters yet. Create your first one!</p>
                </div>
              ) : (
                <div className="divide-y divide-graphite/5">
                  {newsletters.map(newsletter => (
                    <div key={newsletter.id} className="p-6 flex items-center justify-between hover:bg-graphite/[0.02] transition-colors">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-heading text-lg text-graphite-dark">{newsletter.title || newsletter.subject}</h3>
                          <span className={`px-2 py-0.5 rounded text-xs font-body ${NEWSLETTER_STATUS_COLORS[newsletter.status] || 'bg-gray-100 text-gray-800'}`}>
                            {newsletter.status}
                          </span>
                          {newsletter.status === "published" && (
                            <a 
                              href={`/tribe/${newsletter.id}`} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-xs text-sand-dark hover:text-graphite-dark transition-colors"
                              onClick={(e) => e.stopPropagation()}
                            >
                              View Live →
                            </a>
                          )}
                        </div>
                        {newsletter.subtitle && (
                          <p className="font-accent text-sm italic text-graphite/60 mb-1">{newsletter.subtitle}</p>
                        )}
                        <p className="font-body text-sm text-graphite/50 line-clamp-1">{newsletter.content}</p>
                        <p className="font-body text-xs text-graphite/40 mt-2">
                          Created: {new Date(newsletter.created_at).toLocaleDateString()}
                          {newsletter.published_at && ` • Published: ${new Date(newsletter.published_at).toLocaleDateString()}`}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        <button
                          onClick={() => openNewsletterEditor(newsletter)}
                          className="p-2 text-graphite/50 hover:text-graphite-dark transition-colors"
                          data-testid={`edit-newsletter-${newsletter.id}`}
                        >
                          <Edit size={18} />
                        </button>
                        <button
                          onClick={() => handleDeleteNewsletter(newsletter.id)}
                          className="p-2 text-graphite/50 hover:text-red-600 transition-colors"
                          data-testid={`delete-newsletter-${newsletter.id}`}
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Info Box */}
            <div className="mt-6 p-4 bg-[#F5F0EA] rounded-lg border border-sand/30">
              <p className="font-body text-sm text-graphite/70">
                <strong>How it works:</strong> Set status to "Published" to make posts visible on the public{" "}
                <a href="/tribe" target="_blank" rel="noopener noreferrer" className="text-sand-dark hover:underline">/tribe</a> page.
                Draft posts are only visible here in the admin panel.
              </p>
            </div>
          </motion.div>
        )}

        {/* Applications Tab */}
        {activeTab === "applications" && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            <h2 className="font-heading text-3xl tracking-wide uppercase text-graphite-dark mb-8">
              Coaching Applications
            </h2>

            {/* Filters */}
            <div className="flex items-center gap-4 mb-6">
              <div className="relative flex-1 max-w-md">
                <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-graphite/40" />
                <input
                  type="text"
                  placeholder="Search by name or email..."
                  value={applicationSearch}
                  onChange={(e) => setApplicationSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-graphite/20 rounded font-body text-sm focus:outline-none focus:border-sand-dark"
                  data-testid="application-search"
                />
              </div>
              <div className="relative">
                <select
                  value={applicationFilter}
                  onChange={(e) => setApplicationFilter(e.target.value)}
                  className="appearance-none pl-4 pr-10 py-2 border border-graphite/20 rounded font-body text-sm focus:outline-none focus:border-sand-dark bg-white"
                  data-testid="application-filter"
                >
                  <option value="all">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="reviewed">Reviewed</option>
                  <option value="contacted">Contacted</option>
                  <option value="accepted">Accepted</option>
                  <option value="declined">Declined</option>
                </select>
                <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-graphite/40 pointer-events-none" />
              </div>
            </div>

            {/* Applications List */}
            <div className="bg-white rounded-lg border border-graphite/5 overflow-hidden">
              {filteredApplications.length === 0 ? (
                <div className="p-12 text-center">
                  <FileText size={48} className="text-graphite/20 mx-auto mb-4" />
                  <p className="font-body text-graphite/50">No applications found.</p>
                </div>
              ) : (
                <div className="divide-y divide-graphite/5">
                  {filteredApplications.map(app => (
                    <div 
                      key={app.id} 
                      className="p-6 flex items-center justify-between hover:bg-graphite/[0.02] transition-colors cursor-pointer"
                      onClick={() => { setSelectedApplication(app); setApplicationNotes(app.notes || ""); }}
                      data-testid={`application-row-${app.id}`}
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-1">
                          <h3 className="font-heading text-lg text-graphite-dark">
                            {app.first_name} {app.last_name}
                          </h3>
                          <span className={`px-2 py-0.5 rounded text-xs font-body ${STATUS_COLORS[app.status] || STATUS_COLORS.pending}`}>
                            {app.status || "pending"}
                          </span>
                        </div>
                        <p className="font-body text-sm text-graphite/60">{app.email}</p>
                        <div className="flex items-center gap-4 mt-2">
                          <span className="font-body text-xs text-graphite/40">{app.location}</span>
                          <span className="font-body text-xs text-graphite/40">{app.age_range}</span>
                          <span className="font-body text-xs text-graphite/40">
                            {new Date(app.created_at).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                      <Eye size={18} className="text-graphite/40" />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* Subscribers Tab */}
        {activeTab === "subscribers" && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            <h2 className="font-heading text-3xl tracking-wide uppercase text-graphite-dark mb-8">
              Newsletter Subscribers
            </h2>

            <div className="bg-white rounded-lg border border-graphite/5 overflow-hidden">
              {subscribers.length === 0 ? (
                <div className="p-12 text-center">
                  <Users size={48} className="text-graphite/20 mx-auto mb-4" />
                  <p className="font-body text-graphite/50">No subscribers yet.</p>
                </div>
              ) : (
                <>
                  <div className="p-4 bg-graphite/[0.02] border-b border-graphite/5">
                    <p className="font-body text-sm text-graphite/60">
                      Total Subscribers: <strong className="text-graphite-dark">{subscribers.length}</strong>
                    </p>
                  </div>
                  <div className="divide-y divide-graphite/5">
                    {subscribers.map(sub => (
                      <div key={sub.id} className="p-4 flex items-center justify-between">
                        <div>
                          <p className="font-body text-sm text-graphite-dark">{sub.email}</p>
                          {sub.first_name && (
                            <p className="font-body text-xs text-graphite/50">{sub.first_name}</p>
                          )}
                        </div>
                        <div className="text-right">
                          <span className={`px-2 py-0.5 rounded text-xs font-body ${sub.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                            {sub.status}
                          </span>
                          <p className="font-body text-xs text-graphite/40 mt-1">
                            {new Date(sub.subscribed_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </motion.div>
        )}
      </main>

      {/* Newsletter Editor Modal */}
      <AnimatePresence>
        {showNewsletterEditor && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowNewsletterEditor(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-lg w-full max-w-4xl max-h-[95vh] overflow-y-auto"
              onClick={e => e.stopPropagation()}
              data-testid="newsletter-editor-modal"
            >
              <div className="p-6 border-b border-graphite/5 flex items-center justify-between">
                <h3 className="font-heading text-xl tracking-wide uppercase text-graphite-dark">
                  {editingNewsletter ? "Edit Post" : "New Post"}
                </h3>
                <button
                  onClick={() => setShowNewsletterEditor(false)}
                  className="p-2 text-graphite/50 hover:text-graphite-dark transition-colors"
                >
                  <X size={20} />
                </button>
              </div>
              <div className="p-6 space-y-6">
                {/* Title */}
                <div>
                  <label className="font-body text-xs tracking-[0.15em] uppercase text-graphite/50 mb-2 block">
                    Title *
                  </label>
                  <input
                    type="text"
                    value={newsletterForm.title}
                    onChange={(e) => setNewsletterForm({ ...newsletterForm, title: e.target.value })}
                    placeholder="Enter a compelling title..."
                    className="w-full px-4 py-3 border border-graphite/20 rounded font-heading text-xl focus:outline-none focus:border-sand-dark"
                    data-testid="newsletter-subject-input"
                  />
                </div>

                {/* Subtitle */}
                <div>
                  <label className="font-body text-xs tracking-[0.15em] uppercase text-graphite/50 mb-2 block">
                    Subtitle (optional)
                  </label>
                  <input
                    type="text"
                    value={newsletterForm.subtitle}
                    onChange={(e) => setNewsletterForm({ ...newsletterForm, subtitle: e.target.value })}
                    placeholder="A brief teaser or context..."
                    className="w-full px-4 py-3 border border-graphite/20 rounded font-accent italic focus:outline-none focus:border-sand-dark"
                    data-testid="newsletter-subtitle-input"
                  />
                </div>

                {/* Content */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="font-body text-xs tracking-[0.15em] uppercase text-graphite/50">
                      Content *
                    </label>
                    <span className="font-body text-xs text-graphite/40">
                      Supports: ## Headings, ### Subheadings, &gt; Quotes, - Lists
                    </span>
                  </div>
                  <textarea
                    value={newsletterForm.content}
                    onChange={(e) => setNewsletterForm({ ...newsletterForm, content: e.target.value })}
                    placeholder="Write your reflection...

## Use Headings Like This

Write your paragraphs here. Leave a blank line between paragraphs for proper spacing.

> Use this format for inspirational quotes or key takeaways.

### Smaller Sections

- Bullet points work too
- Just use a dash at the start
- Each on a new line"
                    rows={16}
                    className="w-full px-4 py-3 border border-graphite/20 rounded font-body focus:outline-none focus:border-sand-dark resize-none leading-relaxed"
                    data-testid="newsletter-content-input"
                  />
                </div>

                {/* Status */}
                <div className="flex items-center gap-6">
                  <div className="flex-1">
                    <label className="font-body text-xs tracking-[0.15em] uppercase text-graphite/50 mb-2 block">
                      Status
                    </label>
                    <select
                      value={newsletterForm.status}
                      onChange={(e) => setNewsletterForm({ ...newsletterForm, status: e.target.value })}
                      className="w-full px-4 py-3 border border-graphite/20 rounded font-body focus:outline-none focus:border-sand-dark bg-white"
                      data-testid="newsletter-status-select"
                    >
                      <option value="draft">Draft (not visible to public)</option>
                      <option value="published">Published (visible on /tribe)</option>
                    </select>
                  </div>
                </div>

                {/* Info */}
                <div className="p-4 bg-[#F5F0EA] rounded-lg">
                  <p className="font-body text-sm text-graphite/70">
                    <strong>Draft:</strong> Only visible in admin panel. <br />
                    <strong>Published:</strong> Visible on the public <a href="/tribe" target="_blank" rel="noopener noreferrer" className="text-sand-dark hover:underline">/tribe</a> page for all visitors.
                  </p>
                </div>
              </div>
              <div className="p-6 border-t border-graphite/5 flex justify-between items-center">
                <div>
                  {editingNewsletter && editingNewsletter.status === "published" && (
                    <a 
                      href={`/tribe/${editingNewsletter.id}`} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="font-body text-sm text-sand-dark hover:text-graphite-dark transition-colors"
                    >
                      View Live Post →
                    </a>
                  )}
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowNewsletterEditor(false)}
                    className="px-6 py-2 border border-graphite/20 text-graphite-dark rounded font-body text-sm hover:bg-graphite/5 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveNewsletter}
                    disabled={!newsletterForm.title || !newsletterForm.content}
                    className="flex items-center gap-2 px-6 py-2 bg-graphite-dark text-bone rounded font-body text-sm hover:bg-black transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="save-newsletter-btn"
                  >
                    <Save size={16} />
                    {newsletterForm.status === "published" ? "Publish" : "Save Draft"}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Application Detail Modal */}
      <AnimatePresence>
        {selectedApplication && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-6"
            onClick={() => setSelectedApplication(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto"
              onClick={e => e.stopPropagation()}
              data-testid="application-detail-modal"
            >
              <div className="p-6 border-b border-graphite/5 flex items-center justify-between">
                <div>
                  <h3 className="font-heading text-xl tracking-wide uppercase text-graphite-dark">
                    {selectedApplication.first_name} {selectedApplication.last_name}
                  </h3>
                  <p className="font-body text-sm text-graphite/50">{selectedApplication.email}</p>
                </div>
                <button
                  onClick={() => setSelectedApplication(null)}
                  className="p-2 text-graphite/50 hover:text-graphite-dark transition-colors"
                >
                  <X size={20} />
                </button>
              </div>
              
              <div className="p-6 space-y-6">
                {/* Status Update */}
                <div className="flex items-center gap-4 p-4 bg-graphite/[0.02] rounded-lg">
                  <span className="font-body text-sm text-graphite/60">Update Status:</span>
                  <div className="flex gap-2">
                    {["pending", "reviewed", "contacted", "accepted", "declined"].map(status => (
                      <button
                        key={status}
                        onClick={() => handleUpdateApplication(selectedApplication.id, { status })}
                        className={`px-3 py-1 rounded text-xs font-body transition-all ${
                          selectedApplication.status === status 
                            ? STATUS_COLORS[status] + " ring-2 ring-offset-1 ring-graphite/20"
                            : "bg-graphite/5 text-graphite/60 hover:bg-graphite/10"
                        }`}
                        data-testid={`status-${status}`}
                      >
                        {status}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Application Details */}
                <div className="grid grid-cols-2 gap-6">
                  {[
                    { label: "Phone", value: selectedApplication.phone },
                    { label: "Location", value: selectedApplication.location },
                    { label: "Age Range", value: selectedApplication.age_range },
                    { label: "Coaching Interest", value: selectedApplication.coaching_interest },
                    { label: "Training Experience", value: selectedApplication.training_experience, full: true },
                    { label: "Current Goals", value: selectedApplication.current_goals, full: true },
                    { label: "Health Considerations", value: selectedApplication.health_considerations, full: true },
                    { label: "How They Found You", value: selectedApplication.how_did_you_hear, full: true },
                    { label: "Additional Info", value: selectedApplication.additional_info, full: true }
                  ].map((field, i) => field.value && (
                    <div key={i} className={field.full ? "col-span-2" : ""}>
                      <p className="font-body text-xs tracking-[0.15em] uppercase text-graphite/50 mb-1">{field.label}</p>
                      <p className="font-body text-sm text-graphite-dark">{field.value}</p>
                    </div>
                  ))}
                </div>

                {/* Notes */}
                <div>
                  <label className="font-body text-xs tracking-[0.15em] uppercase text-graphite/50 mb-2 block flex items-center gap-2">
                    <MessageSquare size={14} />
                    Admin Notes
                  </label>
                  <textarea
                    value={applicationNotes}
                    onChange={(e) => setApplicationNotes(e.target.value)}
                    placeholder="Add private notes about this application..."
                    rows={4}
                    className="w-full px-4 py-3 border border-graphite/20 rounded font-body text-sm focus:outline-none focus:border-sand-dark resize-none"
                    data-testid="application-notes-input"
                  />
                  <button
                    onClick={() => handleUpdateApplication(selectedApplication.id, { notes: applicationNotes })}
                    className="mt-2 flex items-center gap-2 px-4 py-2 bg-graphite-dark text-bone rounded font-body text-sm hover:bg-black transition-colors"
                    data-testid="save-notes-btn"
                  >
                    <Save size={14} />
                    Save Notes
                  </button>
                </div>
              </div>

              <div className="p-6 border-t border-graphite/5 flex justify-between items-center">
                <p className="font-body text-xs text-graphite/40">
                  Submitted: {new Date(selectedApplication.created_at).toLocaleString()}
                </p>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => handleDeleteApplication(selectedApplication.id)}
                    className="px-4 py-2 border border-red-200 text-red-600 rounded font-body text-sm hover:bg-red-50 transition-colors flex items-center gap-2"
                    data-testid="delete-application-btn"
                  >
                    <Trash2 size={14} />
                    Delete
                  </button>
                  <button
                    onClick={() => setSelectedApplication(null)}
                    className="px-6 py-2 border border-graphite/20 text-graphite-dark rounded font-body text-sm hover:bg-graphite/5 transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
